import React, { createContext, ReactNode } from 'react';
import { BalanceContextValue } from 'src/components/selfService/common/Balance/balance.types';
import { useBalanceAccountInfo } from 'src/components/selfService/common/Balance/hooks/useBalanceAccountInfo';
import { useFetchBalanceCreditInfo } from 'src/components/selfService/common/Balance/hooks/useFetchBalanceCreditInfo';
import { useFetchBalancePortalUrl } from 'src/components/selfService/common/Balance/hooks/useFetchBalancePortalUrl';
import { isBalanceAccountOnNetTerms } from 'src/components/selfService/common/Balance/utils/isBalanceAccountOnNetTerms';

export const defaultBalanceContextValue: BalanceContextValue = {
  externalBalanceBuyerId: null,
  isCreditInfoLoading: false,
  hasCreditInfoError: false,
  creditInfo: null,
  isPortalUrlLoading: false,
  hasPortalUrlError: false,
  portalUrl: null,
  isOnNetTerms: null,
};

export const BalanceContext = createContext<BalanceContextValue | null>(null);

type BalanceProviderProps = {
  children: ReactNode;
};

export const BalanceProvider = ({
  children,
}: BalanceProviderProps): JSX.Element => {
  const { isBalanceAccount, externalBalanceBuyerId } = useBalanceAccountInfo();

  const {
    isLoading: isCreditInfoLoading,
    hasError: hasCreditInfoError,
    balanceCreditInfo: creditInfo,
  } = useFetchBalanceCreditInfo(externalBalanceBuyerId);

  const {
    isLoading: isPortalUrlLoading,
    hasError: hasPortalUrlError,
    balancePortalUrl: portalUrl,
  } = useFetchBalancePortalUrl(externalBalanceBuyerId);

  const isOnNetTerms =
    isBalanceAccount && creditInfo
      ? isBalanceAccountOnNetTerms(creditInfo)
      : null;

  const value: BalanceContextValue = isBalanceAccount
    ? {
        externalBalanceBuyerId,
        isCreditInfoLoading,
        hasCreditInfoError,
        creditInfo,
        isPortalUrlLoading,
        hasPortalUrlError,
        portalUrl,
        isOnNetTerms,
      }
    : {
        ...defaultBalanceContextValue,
        externalBalanceBuyerId,
      };

  return (
    <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>
  );
};
